import * as React from "react";
import { format, parseISO } from "date-fns";
import { Droplets, FlaskConical, Heart, Thermometer } from "lucide-react";
import {
  cn,
  formatTemperature,
  getCyclePhaseSpans,
  normalizeOvulationTest,
  isPostPeakPhaseDay,
  type CyclePhaseKey,
  type PhaseSpan,
} from "@nfp-tracker/utils";
import { Button } from "../ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card";
import {
  DEFAULT_VISIBLE_PHASE_BANDS,
  PhaseBandLegend,
  PhaseBandStack,
  describeDayPhases,
} from "./cycle-phase-band";
import {
  DEFAULT_VISIBLE_ICON_TYPES,
  ICON_TYPE_OPTIONS,
  ObservationCellIcons,
  getObservationCellStyle,
  getSymptomHighlightClass,
} from "./observation-day-cell";
import { SYMPTOM_OPTIONS } from "./symptom-options";

export interface CycleViewObservation {
  date: string;
  bleeding: number | null;
  monitor: string | null;
  intercourse: boolean;
  cycle_day: number;
  mucus: string | null;
  mucus_consistency?: string | null;
  notes: string | null;
  symptoms: string[] | null;
  temperature_f: number | null;
  ovulation_test?: string | null;
}

const ICON_TYPE_GLYPHS: Record<string, React.ReactNode> = {
  bleeding: <Droplets className="h-2.5 w-2.5 text-red-600" />,
  monitor: (
    <span className="flex items-center gap-0.5">
      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-violet-700 text-[9px] font-bold text-white">
        P
      </span>
      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-700 text-[9px] font-bold text-white">
        H
      </span>
      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-700 text-[9px] font-bold text-white">
        L
      </span>
    </span>
  ),
  ovulation_test: <FlaskConical className="h-2.5 w-2.5 text-fuchsia-600" />,
  temperature: <Thermometer className="h-2.5 w-2.5 text-emerald-700" />,
  mucus: <Droplets className="h-2.5 w-2.5 text-teal-500" />,
  intercourse: <Heart className="h-2.5 w-2.5 text-pink-700" />,
};

export interface CycleViewDay {
  date: string;
  cycleDay: number;
  isToday?: boolean;
  observation?: CycleViewObservation | null;
}

export interface CycleViewCycle {
  id: string;
  label: string;
  startDate: string;
  endDate?: string | null;
  days: CycleViewDay[];
  firstPeakCd?: number | null;
  /**
   * Phase spans for the band rows. Supply them wherever the selected methods, postpartum
   * state and cycle history are known — the fertile window rules depend on all three, and
   * this component can't see them. Without them the spans fall back to this cycle's own days
   * under default (Marquette) rules.
   */
  phaseSpans?: PhaseSpan[];
}

interface CycleViewProps {
  cycles: CycleViewCycle[];
  hasMoreCycles?: boolean;
  isLoadingMore?: boolean;
  hoverEnabled?: boolean;
  onLoadMoreCycles?: () => void;
  onDaySelect?: (day: CycleViewDay, cycle: CycleViewCycle) => void;
  className?: string;
  daySize?: number;
  autoFocusCurrentDay?: boolean;
  renderMode?: "interactive" | "print";
  showLegend?: boolean;
  visibleTypes?: string[];
  activeSymptomFilters?: string[];
  onToggleSymptomFilter?: (symptom: string) => void;
  /** Phase bands drawn above each day square. Defaults to fertile window + post-peak. */
  visiblePhases?: CyclePhaseKey[];
  onTogglePhase?: (key: CyclePhaseKey) => void;
  /** Fill the parent's height and confine scrolling to the grid itself (sticky header stays put). Off by default. */
  fillHeight?: boolean;
}

export interface ObservationLegendProps {
  visibleTypes?: string[];
  onToggleType?: (key: string) => void;
  activeSymptomFilters?: string[];
  onToggleSymptomFilter?: (symptom: string) => void;
  visiblePhases?: CyclePhaseKey[];
  onTogglePhase?: (key: CyclePhaseKey) => void;
  /** Skip the outer bordered box — for nesting inside another card instead of showing its own. */
  bare?: boolean;
}

/** Shared active/inactive treatment for every toggle chip below — one visual language for both rows. */
const toggleChipClass = (active: boolean) =>
  cn(
    "inline-flex items-center gap-1 rounded border px-2 py-1 transition-colors",
    active
      ? "border-amber-500 bg-amber-500/20 text-amber-700"
      : "border-border/40 bg-muted/30 text-muted-foreground/50 hover:border-amber-400/60"
  );

/** Static color key plus toggleable detail/symptom chips. Shared by the cycle grid and month grid. */
export const ObservationLegend = ({
  visibleTypes = DEFAULT_VISIBLE_ICON_TYPES,
  onToggleType,
  activeSymptomFilters = [],
  onToggleSymptomFilter,
  visiblePhases = DEFAULT_VISIBLE_PHASE_BANDS,
  onTogglePhase,
  bare = false,
}: ObservationLegendProps) => (
  <div className={cn(!bare && "rounded-lg border bg-muted/30 p-3")}>
    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold">
      <span>Key</span>
      <span className="inline-flex items-center rounded border border-red-300/60 bg-red-300/20 px-2 py-0.5 text-[11px] text-red-700">
        Light bleeding
      </span>
      <span className="inline-flex items-center rounded border border-red-500/60 bg-red-500/25 px-2 py-0.5 text-[11px] text-red-700">
        Medium bleeding
      </span>
      <span className="inline-flex items-center rounded border border-red-800/60 bg-red-800/30 px-2 py-0.5 text-[11px] text-red-900">
        Heavy bleeding
      </span>
      <span className="inline-flex items-center rounded border border-primary/40 bg-primary/15 px-2 py-0.5 text-[11px] text-primary">
        Fertile signal
      </span>
      <span className="inline-flex items-center rounded border border-slate-400/60 bg-slate-400/20 px-2 py-0.5 text-[11px] text-slate-700">
        Post-peak mucus (non-fertile)
      </span>
    </div>

    <div className="flex flex-wrap items-center gap-2 text-[11px]">
      {ICON_TYPE_OPTIONS.map((option) => {
        const active = visibleTypes.includes(option.key);
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onToggleType?.(option.key)}
            aria-pressed={active}
            className={toggleChipClass(active)}
          >
            {ICON_TYPE_GLYPHS[option.key]}
            {option.label}
          </button>
        );
      })}
    </div>

    <div className="mt-2">
      <PhaseBandLegend visiblePhases={visiblePhases} onTogglePhase={onTogglePhase} />
    </div>

    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
      <span className="font-semibold text-muted-foreground">Symptoms</span>
      {SYMPTOM_OPTIONS.map((symptom) => {
        const active = activeSymptomFilters.includes(symptom.name);
        return (
          <button
            key={symptom.name}
            type="button"
            onClick={() => onToggleSymptomFilter?.(symptom.name)}
            aria-pressed={active}
            className={toggleChipClass(active)}
          >
            <symptom.icon className="h-3 w-3" />
            {symptom.name}
          </button>
        );
      })}
    </div>
  </div>
);

export const CycleView = ({
  cycles,
  hasMoreCycles = false,
  isLoadingMore = false,
  hoverEnabled = true,
  onLoadMoreCycles,
  onDaySelect,
  className,
  daySize = 60,
  autoFocusCurrentDay = false,
  renderMode = "interactive",
  showLegend = true,
  visibleTypes = DEFAULT_VISIBLE_ICON_TYPES,
  activeSymptomFilters = [],
  onToggleSymptomFilter,
  visiblePhases = DEFAULT_VISIBLE_PHASE_BANDS,
  onTogglePhase,
  fillHeight = false,
}: CycleViewProps) => {
  const maxCycleDay = cycles.reduce(
    (max, cycle) => Math.max(max, cycle.days.length),
    0
  );
  const currentDayRef = React.useRef<HTMLButtonElement | null>(null);
  const hasAutoFocusedRef = React.useRef(false);

  React.useEffect(() => {
    if (
      !autoFocusCurrentDay ||
      renderMode !== "interactive" ||
      hasAutoFocusedRef.current ||
      !currentDayRef.current
    ) {
      return;
    }

    hasAutoFocusedRef.current = true;
    currentDayRef.current.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
  }, [autoFocusCurrentDay, renderMode, cycles]);

  if (!cycles.length) {
    return (
      <div className={cn("rounded-lg border p-6 text-center", className)}>
        <p className="text-sm text-muted-foreground">No cycles available yet.</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        fillHeight ? "flex min-h-0 flex-col gap-3" : "space-y-3",
        className
      )}
    >
      {showLegend && (
        <ObservationLegend
          visibleTypes={visibleTypes}
          activeSymptomFilters={activeSymptomFilters}
          onToggleSymptomFilter={onToggleSymptomFilter}
          visiblePhases={visiblePhases}
          onTogglePhase={onTogglePhase}
        />
      )}

      <div className={cn("overflow-auto rounded-lg border bg-card", fillHeight && "min-h-0 flex-1")}>
        <div className="min-w-max">
          <div className="sticky top-0 z-20 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
            <div className="flex">
              <div className="sticky left-0 z-30 flex w-28 flex-none items-center border-r bg-card px-3 py-2 text-xs font-semibold text-muted-foreground">
                Days
              </div>
              <div className="flex">
                {Array.from({ length: maxCycleDay }).map((_, index) => (
                  <div
                    key={`header-day-${index + 1}`}
                    className="flex flex-none items-center justify-center border-r text-[11px] font-semibold text-muted-foreground"
                    style={{ width: daySize, height: daySize }}
                  >
                    {index + 1}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="divide-y">
            {cycles.map((cycle) => {
              const phaseSpans =
                cycle.phaseSpans ??
                getCyclePhaseSpans(
                  {
                    start_date: cycle.startDate,
                    end_date: cycle.endDate,
                    first_peak_cd: cycle.firstPeakCd,
                  },
                  cycle.days.flatMap((day) =>
                    day.observation ? [{ ...day.observation, cycle_day: day.cycleDay }] : []
                  )
                );

              return (
              <div key={cycle.id} className="flex">
                <div className="sticky left-0 z-10 flex w-28 flex-none flex-col justify-center border-r bg-card px-3 py-2">
                  <span className="text-xs font-semibold">{cycle.label}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {format(parseISO(cycle.startDate), "MMM d")}
                  </span>
                </div>

                <div className="flex py-1">
                  {cycle.days.map((day) => {
                    const obs = day.observation;
                    // The post-Peak phase span, not Peak + 3: it honours the method's own close
                    // (e.g. the Sympto-Thermal double check) and stops at an unmarked next period.
                    const isPostPeakNonFertile = isPostPeakPhaseDay(day.cycleDay, phaseSpans);
                    const commonClasses = cn(
                      "m-1 flex flex-none flex-col items-center justify-center overflow-hidden rounded-md border text-xs transition-colors",
                      renderMode === "interactive" &&
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      renderMode === "interactive" && hoverEnabled && "hover:border-primary",
                      getObservationCellStyle(obs, { isPostPeakNonFertile }),
                      day.isToday && "ring-2 ring-primary/60",
                      getSymptomHighlightClass(obs, activeSymptomFilters)
                    );

                    const dayPhases = describeDayPhases(day.cycleDay, phaseSpans, visiblePhases);
                    // Bands are decorative (aria-hidden), so the phase names ride along on the
                    // day's own label instead of being announced as separate elements.
                    const dayLabel = dayPhases.length
                      ? `Cycle day ${day.cycleDay} on ${day.date} — ${dayPhases.join(", ")}`
                      : `Cycle day ${day.cycleDay} on ${day.date}`;

                    // Fixed-height track so days with fewer bands don't sit higher than their
                    // neighbours; bands hug the square by bottom-aligning inside it.
                    const bandTrack = visiblePhases.length ? (
                      <div className="flex h-2 w-full flex-none flex-col justify-end">
                        <PhaseBandStack
                          cycleDay={day.cycleDay}
                          spans={phaseSpans}
                          visiblePhases={visiblePhases}
                        />
                      </div>
                    ) : null;

                    // Wrapper spans the full column width so consecutive days' band segments
                    // touch — the squares carry their own margin, which would break the line.
                    const wrapDay = (cell: React.ReactNode) => (
                      <div
                        key={`${cycle.id}-${day.date}`}
                        className="flex flex-none flex-col items-center"
                        style={{ width: daySize }}
                      >
                        {bandTrack}
                        {cell}
                      </div>
                    );

                    if (renderMode === "print") {
                      return wrapDay(
                        <div
                          className={commonClasses}
                          style={{ width: daySize - 8, height: daySize - 8 }}
                        >
                          <span className="text-[10px] font-semibold">{day.cycleDay}</span>
                          <ObservationCellIcons observation={obs} size="sm" visibleTypes={visibleTypes} activeSymptomFilters={activeSymptomFilters} />
                        </div>
                      );
                    }

                    if (!hoverEnabled) {
                      return wrapDay(
                        <button
                          ref={day.isToday ? currentDayRef : null}
                          type="button"
                          onClick={() => onDaySelect?.(day, cycle)}
                          className={commonClasses}
                          style={{ width: daySize - 8, height: daySize - 8 }}
                          aria-label={dayLabel}
                        >
                          <span className="text-[10px] font-semibold leading-none">{day.cycleDay}</span>
                          <ObservationCellIcons observation={obs} size="sm" visibleTypes={visibleTypes} activeSymptomFilters={activeSymptomFilters} />
                        </button>
                      );
                    }

                    return wrapDay(
                      <HoverCard openDelay={140}>
                        <HoverCardTrigger asChild>
                          <button
                            ref={day.isToday ? currentDayRef : null}
                            type="button"
                            onClick={() => onDaySelect?.(day, cycle)}
                            className={commonClasses}
                            style={{ width: daySize - 8, height: daySize - 8 }}
                            aria-label={dayLabel}
                          >
                            <span className="text-[10px] font-semibold leading-none">{day.cycleDay}</span>
                            <ObservationCellIcons observation={obs} size="sm" visibleTypes={visibleTypes} activeSymptomFilters={activeSymptomFilters} />
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-72" align="start">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold">
                                {format(parseISO(day.date), "EEEE, MMM d")}
                              </p>
                              <p className="text-xs text-muted-foreground">CD {day.cycleDay}</p>
                            </div>

                            {dayPhases.length ? (
                              <p className="text-xs font-medium text-muted-foreground">
                                {dayPhases.join(" · ")}
                              </p>
                            ) : null}

                            {obs ? (
                              <div className="space-y-1 text-xs">
                                {obs.temperature_f ? (
                                  <p>Temperature: {formatTemperature(obs.temperature_f, "°F")}</p>
                                ) : null}
                                {obs.bleeding ? <p>Bleeding: {obs.bleeding}</p> : null}
                                {obs.monitor ? <p>Monitor: {obs.monitor}</p> : null}
                                {normalizeOvulationTest(obs.ovulation_test) ? (
                                  <p>
                                    Ovulation test:{" "}
                                    {normalizeOvulationTest(obs.ovulation_test) === "positive"
                                      ? "Positive"
                                      : "Negative"}
                                  </p>
                                ) : null}
                                {obs.mucus ? <p>Mucus: {obs.mucus}</p> : null}
                                <p>Intercourse: {obs.intercourse ? "Yes" : "No"}</p>
                                {obs.symptoms && obs.symptoms.length > 0 ? (
                                  <p>Symptoms: {obs.symptoms.join(", ")}</p>
                                ) : null}
                                {obs.notes ? (
                                  <p className="line-clamp-3 text-muted-foreground">Notes: {obs.notes}</p>
                                ) : null}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                No observation for this day yet.
                              </p>
                            )}
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>

      {renderMode === "interactive" && onLoadMoreCycles && (hasMoreCycles || isLoadingMore) ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMoreCycles}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? "Loading..." : "Load older cycles"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
